import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import Node, { InputType } from 'components_new/molecules/Node'
import AddButton from 'components_new/molecules/Node/AddButton'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FunctionSelect from 'components_new/molecules/Node/FunctionSelect'
import NameField from 'components_new/molecules/Node/NameField'
import SideBadge from 'components_new/molecules/Node/SideBadge'

import NodeType from 'types/transformation/enums/NodeType'
import {
  ConcatenateNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import { getNewColumn } from './utils'
import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'

const DELIMITERS = [
  { id: '-', name: 'Bindestreck' },
  { id: '', name: 'Inget tecken' },
  { id: ' ', name: 'Mellanslag' }
]

interface ConcatenateNodeProps {
  id: string
  data: ConcatenateNode
  selected: boolean
}

const Concatenate = (props: ConcatenateNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  // Which source table each Input node reads, so a column can show its origin
  // table name (not just the node number) in the field selector.
  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<ConcatenateNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      columns: nodeData.columns.map((col) => ({
        ...col,
        left_column: pruneRef(col.left_column, inputSchema),
        right_column: pruneRef(col.right_column, inputSchema)
      }))
    }),
    [inputSchema]
  )

  const handleSelected = useCallback(
    (
      columnIndex: number,
      newValue: string,
      prop: 'left_column' | 'right_column' | 'delimiter' | 'name'
    ) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as ConcatenateNode

          newNodeData.columns[columnIndex][prop] = newValue

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const handleAdd = useCallback(() => {
    setNodes((existingNodes) =>
      existingNodes.map((node) => {
        if (node.id !== id) {
          return node
        }

        const newNodeData = cloneDeep(node.data) as ConcatenateNode

        newNodeData.columns.push(getNewColumn(newNodeData))

        return {
          ...node,
          data: newNodeData
        }
      })
    )
  }, [setNodes, id])

  const handleRemove = useCallback(
    (columnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as ConcatenateNode

          newNodeData.columns = newNodeData.columns.filter(
            (col) => col.id !== columnId
          )

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="Merge"
      inputType={InputType.SINGLE}
      title={'Concatenate'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.CONCATENATE}
    >
      <Box sx={{ width: '288px' }}>
        {data.columns.map((column, index) => (
          <Box key={column.id}>
            {index > 0 ? <Divider gutter /> : null}

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Caption>Källkolumner</Caption>
              <IconButton
                onClick={() => handleRemove(column.id)}
                size="small"
                sx={{ mb: 0.5 }}
              >
                <Icon name="Close" sx={{ fontSize: '15px' }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '7px',
                mb: 0.5
              }}
            >
              <SideBadge label="A" />
              <FieldSelect
                value={column.left_column}
                options={fieldOptions}
                onChange={(val) => handleSelected(index, val, 'left_column')}
                placeholder="Välj attribut"
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '7px' }}>
              <SideBadge label="B" />
              <FieldSelect
                value={column.right_column}
                options={fieldOptions}
                onChange={(val) => handleSelected(index, val, 'right_column')}
                placeholder="Välj attribut"
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Caption>Separator</Caption>
            </Box>
            <FunctionSelect
              value={column.delimiter ?? ''}
              options={DELIMITERS}
              placeholder="Välj separator"
              onChange={(delimiter) =>
                handleSelected(index, delimiter, 'delimiter')
              }
              sx={{ width: '100%' }}
            />

            <Box sx={{ mt: 1.5 }}>
              <Caption>Nytt kolumnnamn</Caption>
            </Box>
            <NameField
              value={column.name}
              onChange={(name) => handleSelected(index, name, 'name')}
            />
          </Box>
        ))}

        <Box sx={{ mt: 1 }}>
          <AddButton onClick={handleAdd} />
        </Box>
      </Box>
    </Node>
  )
}

export default Concatenate
